import { useState, useEffect, useCallback } from 'react';
import { Palette, Type, Save, Eye, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Creator & Explorer archetype palette — earthy, warm, adventurous
const COLOR_PRESETS = [
  { name: 'Terracotta Trail', color: '#9A3412', desc: 'Warm earth' },
  { name: 'Temple Red', color: '#B91C1C', desc: 'Sacred monasteries' },
  { name: 'Golden Hour', color: '#B45309', desc: 'Amber sunrise' },
  { name: 'Jungle Canopy', color: '#166534', desc: 'Deep forest' },
  { name: 'Himalayan Sky', color: '#1E40AF', desc: 'Mountain blue' },
  { name: 'Summit Stone', color: '#44403C', desc: 'Earthy neutral' },
];

const FONT_OPTIONS = [
  { label: 'Georgia (Classic)', value: 'Georgia, serif' },
  { label: 'Palatino (Elegant)', value: '"Palatino Linotype", serif' },
  { label: 'Verdana (Clean)', value: 'Verdana, sans-serif' },
  { label: 'Trebuchet (Modern)', value: '"Trebuchet MS", sans-serif' },
];

const TEMPLATE_TYPES = [
  { key: 'welcome', label: 'Welcome Email', desc: 'Sent when someone subscribes' },
  { key: 'new_post', label: 'New Post', desc: 'Sent when you publish a story' },
  { key: 'contact', label: 'Contact Alert', desc: 'Sent to you on new messages' },
];

const EmailSettingsTab = ({ token }) => {
  const [settings, setSettings] = useState({
    primary_color: '#9A3412',
    header_image: '',
    footer_text: 'Whispers of Nepal',
    font_family: 'Georgia, serif',
  });
  const [templates, setTemplates] = useState({});
  const [activeTemplate, setActiveTemplate] = useState('welcome');
  const [previewHtml, setPreviewHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/email`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.settings) {
        setSettings({
          primary_color: res.data.settings.primary_color || '#9A3412',
          header_image: res.data.settings.header_image || '',
          footer_text: res.data.settings.footer_text || 'Whispers of Nepal',
          font_family: res.data.settings.font_family || 'Georgia, serif',
        });
      }
      if (res.data.templates) {
        setTemplates(res.data.templates);
      }
    } catch (err) {
      console.error('Failed to fetch email settings:', err);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/email`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Email design saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplate = async () => {
    const tpl = templates[activeTemplate];
    if (!tpl) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/settings/email/template`,
        { template_type: activeTemplate, ...tpl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Template "${activeTemplate}" saved`);
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const res = await axios.get(`${API}/settings/email/preview/${activeTemplate}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreviewHtml(res.data.html);
    } catch (err) {
      console.error('Preview failed:', err);
    } finally {
      setLoadingPreview(false);
    }
  }, [activeTemplate, token]);

  useEffect(() => {
    loadPreview();
  }, [activeTemplate, loadPreview]);

  const updateTemplate = (field, value) => {
    setTemplates((prev) => ({
      ...prev,
      [activeTemplate]: { ...prev[activeTemplate], [field]: value },
    }));
  };

  const currentTpl = templates[activeTemplate] || {};

  return (
    <div className="space-y-6" data-testid="email-settings-tab">
      {/* Design Settings */}
      <div className="bg-white p-6 rounded-sm border border-stone-200">
        <h3 className="text-sm font-medium text-stone-700 mb-4 flex items-center gap-2">
          <Palette size={15} /> Email Design
        </h3>

        {/* Color Presets */}
        <div className="mb-5">
          <label className="block text-xs text-stone-500 uppercase tracking-wider mb-2">Brand Color</label>
          <div className="flex flex-wrap gap-2" data-testid="color-presets">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset.color}
                onClick={() => setSettings((s) => ({ ...s, primary_color: preset.color }))}
                className={`flex items-center gap-2 px-3 py-2 rounded-sm border text-xs transition-all ${
                  settings.primary_color === preset.color
                    ? 'border-stone-900 bg-stone-50 ring-1 ring-stone-900'
                    : 'border-stone-200 hover:border-stone-400'
                }`}
                title={preset.desc}
                data-testid={`color-preset-${preset.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: preset.color }} />
                <span className="text-stone-700">{preset.name}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <label className="text-xs text-stone-400">Custom:</label>
            <input
              type="color"
              value={settings.primary_color}
              onChange={(e) => setSettings((s) => ({ ...s, primary_color: e.target.value }))}
              className="w-8 h-8 rounded cursor-pointer border border-stone-200"
              data-testid="custom-color-picker"
            />
            <span className="text-xs text-stone-500 font-mono">{settings.primary_color}</span>
          </div>
        </div>

        {/* Font */}
        <div className="mb-5">
          <label className="block text-xs text-stone-500 uppercase tracking-wider mb-2">
            <Type size={12} className="inline mr-1" /> Font Family
          </label>
          <div className="flex flex-wrap gap-2" data-testid="font-selector">
            {FONT_OPTIONS.map((f) => (
              <button
                key={f.value}
                onClick={() => setSettings((s) => ({ ...s, font_family: f.value }))}
                className={`px-3 py-2 rounded-sm border text-xs transition-all ${
                  settings.font_family === f.value
                    ? 'border-stone-900 bg-stone-50 ring-1 ring-stone-900'
                    : 'border-stone-200 hover:border-stone-400'
                }`}
                style={{ fontFamily: f.value }}
                data-testid={`font-option-${f.label.split(' ')[0].toLowerCase()}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Footer Text */}
        <div className="mb-5">
          <label className="block text-xs text-stone-500 uppercase tracking-wider mb-2">Footer Text</label>
          <input
            type="text"
            value={settings.footer_text}
            onChange={(e) => setSettings((s) => ({ ...s, footer_text: e.target.value }))}
            className="w-full md:w-1/2 px-3 py-2 border border-stone-200 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-stone-400"
            data-testid="footer-text-input"
          />
        </div>

        {/* Header Image */}
        <div className="mb-5">
          <label className="block text-xs text-stone-500 uppercase tracking-wider mb-2">Header Image URL</label>
          <input
            type="url"
            value={settings.header_image || ''}
            onChange={(e) => setSettings((s) => ({ ...s, header_image: e.target.value }))}
            placeholder="https://..."
            className="w-full md:w-2/3 px-3 py-2 border border-stone-200 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-stone-400"
            data-testid="header-image-input"
          />
        </div>

        <Button
          onClick={handleSaveSettings}
          disabled={saving}
          className="bg-stone-900 hover:bg-stone-800 text-stone-50"
          data-testid="save-design-btn"
        >
          <Save size={14} className="mr-2" />
          {saving ? 'Saving...' : 'Save Design'}
        </Button>
      </div>

      {/* Template Editor */}
      <div className="bg-white rounded-sm border border-stone-200">
        <div className="border-b border-stone-200 px-6 py-4">
          <h3 className="text-sm font-medium text-stone-700 mb-3">Email Templates</h3>
          <div className="flex gap-1" data-testid="template-type-selector">
            {TEMPLATE_TYPES.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTemplate(t.key)}
                className={`px-3 py-1.5 text-xs rounded-sm transition-colors ${
                  activeTemplate === t.key
                    ? 'bg-stone-900 text-stone-50'
                    : 'text-stone-600 hover:bg-stone-100'
                }`}
                data-testid={`template-btn-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-stone-400 mt-2">
            {TEMPLATE_TYPES.find((t) => t.key === activeTemplate)?.desc}
          </p>
        </div>

        <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-stone-200">
          {/* Edit Fields */}
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1">Subject</label>
              <input
                type="text"
                value={currentTpl.subject || ''}
                onChange={(e) => updateTemplate('subject', e.target.value)}
                className="w-full px-3 py-2 border border-stone-200 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-stone-400"
                data-testid="template-subject-input"
              />
            </div>
            <div>
              <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1">Heading</label>
              <input
                type="text"
                value={currentTpl.heading || ''}
                onChange={(e) => updateTemplate('heading', e.target.value)}
                className="w-full px-3 py-2 border border-stone-200 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-stone-400"
                data-testid="template-heading-input"
              />
            </div>
            <div>
              <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1">Body</label>
              <textarea
                rows={4}
                value={currentTpl.body || ''}
                onChange={(e) => updateTemplate('body', e.target.value)}
                className="w-full px-3 py-2 border border-stone-200 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-stone-400 resize-y"
                data-testid="template-body-input"
              />
              <p className="text-xs text-stone-400 mt-1">
                Variables: {'{site_name}'}, {'{post_title}'}, {'{post_excerpt}'}, {'{sender_name}'}
              </p>
            </div>
            {currentTpl.button_text !== undefined && (
              <>
                <div>
                  <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1">Button Text</label>
                  <input
                    type="text"
                    value={currentTpl.button_text || ''}
                    onChange={(e) => updateTemplate('button_text', e.target.value)}
                    className="w-full px-3 py-2 border border-stone-200 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-stone-400"
                    data-testid="template-button-text-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1">Button URL</label>
                  <input
                    type="text"
                    value={currentTpl.button_url || ''}
                    onChange={(e) => updateTemplate('button_url', e.target.value)}
                    className="w-full px-3 py-2 border border-stone-200 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-stone-400"
                    data-testid="template-button-url-input"
                  />
                </div>
              </>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSaveTemplate}
                disabled={saving}
                className="bg-stone-900 hover:bg-stone-800 text-stone-50"
                data-testid="save-template-btn"
              >
                <Save size={14} className="mr-2" />
                {saving ? 'Saving...' : 'Save Template'}
              </Button>
              <Button
                variant="outline"
                onClick={loadPreview}
                disabled={loadingPreview}
                data-testid="refresh-preview-btn"
              >
                <RefreshCw size={14} className={`mr-2 ${loadingPreview ? 'animate-spin' : ''}`} />
                Preview
              </Button>
            </div>
          </div>

          {/* Live Preview */}
          <div className="p-6" data-testid="email-preview-panel">
            <div className="flex items-center gap-2 mb-3">
              <Eye size={14} className="text-stone-500" />
              <h4 className="text-xs text-stone-500 uppercase tracking-wider">Live Preview</h4>
            </div>
            {previewHtml ? (
              <div className="border border-stone-200 rounded-sm overflow-hidden bg-stone-50">
                <iframe
                  title="Email Preview"
                  srcDoc={previewHtml}
                  className="w-full h-[500px] border-0"
                  sandbox=""
                  data-testid="email-preview-iframe"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-[500px] border border-dashed border-stone-200 rounded-sm text-stone-400 text-sm">
                {loadingPreview ? 'Loading preview...' : 'Save settings and click Preview to see your email'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailSettingsTab;
